import { store } from '../store/configureStore';
import { User } from '../data/models/user';
import { getRolesFromJwt } from '@app/data/auth/roles';

/**
 * Centralized authentication service that provides methods for working with user authentication
 */
class AuthService {
  /**
   * Get the current authenticated user
   * First checks Redux store, then falls back to localStorage
   * @returns User object or null if not authenticated
   */
  getCurrentUser(): User | null {
    // First try to get from Redux store (faster, in-memory)
    const storeUser = store.getState().account.user;
    if (storeUser) return storeUser;

    // Fall back to localStorage if not in Redux store
    return this.getUserFromLocalStorage();
  }

  /**
   * Get the current access token
   * @returns Access token string or null if not available
   */
  getAccessToken(): string | null {
    const user = this.getCurrentUser();
    return user?.accessToken || null;
  }

  /**
   * Check if the user is authenticated
   * @returns true if authenticated, false otherwise
   */
  isAuthenticated(): boolean {
    return !!this.getCurrentUser();
  }

  /**
   * Save user data to both Redux store and localStorage
   * @param userData User data to save
   */
  saveUser(userData: User): void {
    // Parse roles from accessToken and assign to userData.roles
    userData.roles = getRolesFromJwt(userData.accessToken);
    // Save to localStorage for persistence
    this.saveUserToLocalStorage(userData);
  }

  /**
   * Reset user data in both Redux store and localStorage
   */
  resetUser(): void {
    this.saveUserToLocalStorage(null);
  }

  /**
   * Get user refresh token and email for token refresh operations
   * @returns Object containing refresh token and email, or null if not available
   */
  getRefreshCredentials(): { refreshToken: string; email: string } | null {
    const user = this.getCurrentUser();
    if (!user || !user.refreshToken || !user.email) return null;

    return {
      refreshToken: user.refreshToken,
      email: user.email,
    };
  }

  // Save user data to localStorage
  saveUserToLocalStorage(userData: User | null): void {
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
  }

  // Get user data from localStorage
  getUserFromLocalStorage(): User | null {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      try {
        return JSON.parse(userJson);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Check if the user has a specific role
   * @param roleName Name of the role to check
   * @param entityId Optional entity ID (e.g., restaurantId) to check role against
   * @returns true if user has the role, false otherwise
   */
  hasRole(roleName: string, entityId?: string): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.roles) return false;

    switch (roleName) {
      case 'SuperAdmin':
        return user.roles.isSuperAdmin;
      case 'RestaurantManager':
        return entityId
          ? user.roles.restaurantManager.some(
              (r) => r.restaurantId === entityId
            )
          : user.roles.restaurantManager.length > 0;
      case 'KitchenStaff':
        return entityId
          ? user.roles.kitchenStaff.some((r) => r.restaurantId === entityId)
          : user.roles.kitchenStaff.length > 0;
      case 'RestaurantWaiter':
        return entityId
          ? user.roles.restaurantWaiter.some((r) => r.restaurantId === entityId)
          : user.roles.restaurantWaiter.length > 0;
      default:
        return false;
    }
  }
}

// Create a singleton instance
const authService = new AuthService();

export default authService;
